import { type ReactElement } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SensitiveRecordBadgeProps {
  readonly className?: string;
}

/**
 * Compact "Sensitive" marker for patient rows in list + search surfaces
 * (Browse Patients, Quick Search) so break-glass records are recognisable
 * before the user clicks in.
 *
 * Reflects only the sensitivity flag — the reason and audit stamps are PHI and
 * never reach these surfaces. The richer, persistent on-record indicator is
 * `features/patient-record/.../BreakGlassBadge`.
 */
export function SensitiveRecordBadge({
  className,
}: SensitiveRecordBadgeProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Badge
      variant="destructive"
      className={cn(
        'inline-flex h-4 shrink-0 items-center gap-1 px-1.5 text-[10px] font-semibold uppercase tracking-wide',
        className,
      )}
      title={t('patient.sensitiveRecord.srLabel')}
    >
      <ShieldAlert className="h-3 w-3" aria-hidden="true" />
      {t('patient.sensitiveRecord.badge')}
    </Badge>
  );
}
